import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { supabase } from '@/lib/supabase'
import { Building2, RefreshCw } from 'lucide-react'
import DashboardKPIs from '@/components/dashboard/DashboardKPIs'
import DashboardObrasTable from '@/components/dashboard/DashboardObrasTable'
import DashboardCurvaS from '@/components/dashboard/DashboardCurvaS'
import DashboardFVSConformidade from '@/components/dashboard/DashboardFVSConformidade'
import DashboardOperacional from '@/components/dashboard/DashboardOperacional'
import DashboardFinanceiro from '@/components/dashboard/DashboardFinanceiro'
import DashboardAlerts from '@/components/dashboard/DashboardAlerts'
import DashboardFilters, { type PeriodoFiltro } from '@/components/dashboard/DashboardFilters'
import { BarChart3, Activity } from 'lucide-react'

export default function DashboardPage() {
  const { obras, setObraAtiva, company } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)

  // Filters
  const [periodo, setPeriodo] = useState<PeriodoFiltro>('mes')
  const [obraFiltro, setObraFiltro] = useState<string>('all')

  // Data
  const [obraDetails, setObraDetails] = useState<any[]>([])
  const [pendVencidas, setPendVencidas] = useState<any[]>([])
  const [ocCriticas, setOcCriticas] = useState<any[]>([])
  const [semRdo, setSemRdo] = useState<any[]>([])
  const [maoDeObraHoje, setMaoDeObraHoje] = useState(0)
  const [fvsTotals, setFvsTotals] = useState({ aprovadas: 0, reprovadas: 0, pendentes: 0 })
  const [fvsPorObra, setFvsPorObra] = useState<Record<string, { aprovadas: number; reprovadas: number; pendentes: number }>>({})
  const [fvsPendPorObra, setFvsPendPorObra] = useState<Record<string, number>>({})
  const [reqPorObra, setReqPorObra] = useState<Record<string, number>>({})
  const [reqEstoqueAbertas, setReqEstoqueAbertas] = useState(0)
  const [itensCriticos, setItensCriticos] = useState(0)
  const [frentes, setFrentes] = useState<any[]>([])
  const [curvasPorObra, setCurvasPorObra] = useState<Record<string, any[]>>({})
  const [curvaConsolidada, setCurvaConsolidada] = useState<any[]>([])
  const [valorComprasAberto, setValorComprasAberto] = useState(0)
  const [rpcKpis, setRpcKpis] = useState<{ total_obras: number; obras_ativas: number; pendencias_abertas: number; rdos_mes: number; fvs_aprovadas: number; ocorrencias_abertas: number } | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const todayStr = new Date().toISOString().split('T')[0]

      // ---- 0. KPIs via RPC ----
      if (company?.id) {
        const { data: kpis } = await supabase.rpc('get_dashboard_kpis', { p_company_id: company.id } as any)
        if (kpis) setRpcKpis(kpis as any)
      }

      // ---- 1. Enriched obra data ----
      const { data: resumos } = await supabase.from('vw_resumo_obras').select('*').order('nome')
      const obrasMap = new Map((resumos || []).map((r: any) => [r.id, r]))
      const enriched = obras.map(o => ({ ...o, ...(obrasMap.get(o.id) || {}) }))

      // ---- 2. Last RDO per obra ----
      const obraIds = obras.map(o => o.id)
      if (obraIds.length > 0) {
        const { data: rdos } = await supabase
          .from('diario_obra').select('obra_id,data_registro,total_trabalhadores')
          .in('obra_id', obraIds).order('data_registro', { ascending: false })

        const lastRdo: Record<string, string> = {}
        let todayWorkers = 0
        ;(rdos || []).forEach((r: any) => {
          if (!lastRdo[r.obra_id]) lastRdo[r.obra_id] = r.data_registro
          if (r.data_registro === todayStr) todayWorkers += (r.total_trabalhadores || 0)
        })
        setMaoDeObraHoje(todayWorkers)

        const todayMs = Date.now()
        enriched.forEach((o: any) => {
          const last = lastRdo[o.id]
          if (last) {
            o._lastRdoDays = Math.floor((todayMs - new Date(last).getTime()) / 86400000)
            o._lastRdoDate = last
          } else {
            o._lastRdoDays = 999
          }
        })
        setSemRdo(enriched.filter((o: any) => o.status === 'em_execucao' && o._lastRdoDays > 3))

        // ---- Frentes de serviço (últimos 7 dias) ----
        const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7)
        const { data: rdosFrentes } = await supabase
          .from('diario_obra').select('obra_id,frente_servico,data_registro')
          .in('obra_id', obraIds).gte('data_registro', weekAgo.toISOString().split('T')[0])
          .not('frente_servico', 'is', null)

        const frentesMap: Record<string, { obraId: string; ultimo: string }> = {}
        ;(rdosFrentes || []).forEach((r: any) => {
          const key = `${r.obra_id}::${r.frente_servico}`
          if (!frentesMap[key] || r.data_registro > frentesMap[key].ultimo) {
            frentesMap[key] = { obraId: r.obra_id, ultimo: r.data_registro }
          }
        })
        const obraNomeMap = new Map(obras.map(o => [o.id, o.nome]))
        setFrentes(Object.entries(frentesMap).map(([key, val]) => {
          const [, nome] = key.split('::')
          return {
            nome,
            obraNome: obraNomeMap.get(val.obraId) || '—',
            ultimoRegistro: val.ultimo,
            diasParado: Math.floor((Date.now() - new Date(val.ultimo).getTime()) / 86400000),
          }
        }).sort((a, b) => b.diasParado - a.diasParado))
      }

      // ---- 3. Responsável ----
      const { data: members } = await supabase.from('profiles').select('id,nome,perfil')
        .in('perfil', ['engenheiro', 'gerente', 'dono'])
      const memberMap = new Map((members || []).map((m: any) => [m.id, m.nome]))
      enriched.forEach((o: any) => { o._engenheiro = memberMap.get(o.criado_por) || '—' })

      setObraDetails(enriched)

      // ---- 4. Pendências vencidas ----
      const { data: pv } = await supabase.from('pendencias').select('id,nome,prazo,obra_id')
        .eq('concluida', false).lt('prazo', todayStr).order('prazo').limit(10)
      setPendVencidas(pv || [])

      // ---- 5. Ocorrências críticas ----
      const { data: oc } = await supabase.from('ocorrencias').select('id,titulo,severidade,obra_id,created_at')
        .eq('resolvida', false).eq('severidade', 'critica').order('created_at', { ascending: false }).limit(10)
      setOcCriticas(oc || [])

      // ---- 6. Acidentes no mês (now handled by RPC) ----

      // ---- 7. FVS stats ----
      const { data: fvsAll } = await supabase.from('fvs').select('id,obra_id,status')
      const fvsData = fvsAll || []
      let ap = 0, rp = 0, pe = 0
      const fvsObraMap: Record<string, { aprovadas: number; reprovadas: number; pendentes: number }> = {}
      const fvsPendMap: Record<string, number> = {}
      fvsData.forEach((f: any) => {
        if (f.status === 'aprovado' || f.status === 'aprovada') ap++
        else if (f.status === 'reprovado' || f.status === 'reprovada') rp++
        else pe++

        if (!fvsObraMap[f.obra_id]) fvsObraMap[f.obra_id] = { aprovadas: 0, reprovadas: 0, pendentes: 0 }
        if (f.status === 'aprovado' || f.status === 'aprovada') fvsObraMap[f.obra_id].aprovadas++
        else if (f.status === 'reprovado' || f.status === 'reprovada') fvsObraMap[f.obra_id].reprovadas++
        else fvsObraMap[f.obra_id].pendentes++

        if (f.status !== 'aprovado' && f.status !== 'aprovada' && f.status !== 'reprovado' && f.status !== 'reprovada') {
          fvsPendMap[f.obra_id] = (fvsPendMap[f.obra_id] || 0) + 1
        }
      })
      setFvsTotals({ aprovadas: ap, reprovadas: rp, pendentes: pe })
      setFvsPorObra(fvsObraMap)
      setFvsPendPorObra(fvsPendMap)

      // ---- 8. Estoque (try/catch for tables that might not exist on external DB) ----
      try {
        const { data: reqs } = await supabase.from('estoque_requisicoes').select('id,obra_id,status')
          .in('status', ['pendente', 'separando'])
        const reqData = reqs || []
        setReqEstoqueAbertas(reqData.length)
        const reqMap: Record<string, number> = {}
        reqData.forEach((r: any) => { reqMap[r.obra_id] = (reqMap[r.obra_id] || 0) + 1 })
        setReqPorObra(reqMap)
      } catch { setReqEstoqueAbertas(0); setReqPorObra({}) }

      try {
        let q = supabase.from('estoque_abaixo_minimo' as any)
          .select('id, nome, saldo_atual, estoque_minimo, deficit')
        if (company?.id) q = q.eq('company_id', company.id)
        const { data: mats } = await q
        setItensCriticos((mats || []).length)
      } catch { setItensCriticos(0) }

      try {
        const { data: compras } = await supabase.from('estoque_lista_compras').select('valor_total,status')
          .in('status', ['pendente', 'em_cotacao'])
        setValorComprasAberto((compras || []).reduce((s: number, c: any) => s + (c.valor_total || 0), 0))
      } catch { setValorComprasAberto(0) }

      // ---- 9. Curva S data (from production/RDO history) ----
      try {
        const { data: producao } = await supabase.from('producao')
          .select('obra_id,data_registro,quantidade_exec,quantidade_prev')
          .in('obra_id', obraIds).order('data_registro')

        // Build monthly curves per obra
        const curves: Record<string, Record<string, { prev: number; real: number }>> = {}
        ;(producao || []).forEach((p: any) => {
          const month = p.data_registro?.substring(0, 7) // YYYY-MM
          if (!month) return
          if (!curves[p.obra_id]) curves[p.obra_id] = {}
          if (!curves[p.obra_id][month]) curves[p.obra_id][month] = { prev: 0, real: 0 }
          curves[p.obra_id][month].real += (p.quantidade_exec || 0)
          curves[p.obra_id][month].prev += (p.quantidade_prev || p.quantidade_exec || 0)
        })

        const curvasObra: Record<string, any[]> = {}
        Object.entries(curves).forEach(([obraId, months]) => {
          const sorted = Object.entries(months).sort(([a], [b]) => a.localeCompare(b))
          let cumPrev = 0, cumReal = 0
          const totalPrev = sorted.reduce((s, [, v]) => s + v.prev, 0) || 1
          const totalReal = sorted.reduce((s, [, v]) => s + v.real, 0) || 1
          curvasObra[obraId] = sorted.map(([mes, v]) => {
            cumPrev += v.prev; cumReal += v.real
            return {
              mes: mes.substring(5) + '/' + mes.substring(2, 4),
              previsto: Math.round((cumPrev / totalPrev) * 100),
              realizado: Math.round((cumReal / totalReal) * 100),
            }
          })
        })
        setCurvasPorObra(curvasObra)

        // Consolidada: use obra avanço físico over time (simplified)
        if (Object.keys(curvasObra).length > 0) {
          const allMonths = new Set<string>()
          Object.values(curvasObra).forEach(arr => arr.forEach(p => allMonths.add(p.mes)))
          const sorted = [...allMonths].sort()
          setCurvaConsolidada(sorted.map(mes => {
            let prevSum = 0, realSum = 0, count = 0
            Object.values(curvasObra).forEach(arr => {
              const pt = arr.find(p => p.mes === mes)
              if (pt) { prevSum += pt.previsto; realSum += pt.realizado; count++ }
            })
            return { mes, previsto: count > 0 ? Math.round(prevSum / count) : 0, realizado: count > 0 ? Math.round(realSum / count) : 0 }
          }))
        }
      } catch { setCurvasPorObra({}); setCurvaConsolidada([]) }

      setLastUpdate(new Date())
    } catch (err) {
      console.error('Dashboard load error:', err)
    }
    setLoading(false)
  }, [obras, company?.id])

  useEffect(() => { load() }, [load])

  // Computed
  const filteredObras = obraFiltro === 'all' ? obraDetails : obraDetails.filter(o => o.id === obraFiltro)
  const obrasAtivas = filteredObras.filter(o => o.status === 'em_execucao')
  const obrasAtrasadas = filteredObras.filter(o =>
    o.status === 'em_execucao' && o.data_termino_prev && new Date(o.data_termino_prev) < new Date()
  )
  const avgAvanco = obrasAtivas.length
    ? Math.round(obrasAtivas.reduce((s, o) => s + (o.avanco_fisico || 0), 0) / obrasAtivas.length)
    : 0
  const totalOcCrit = filteredObras.reduce((s, o) => s + (o.ocorrencias_criticas || 0), 0)
  const totalPendVenc = obraFiltro === 'all' ? pendVencidas.length : pendVencidas.filter(p => p.obra_id === obraFiltro).length
  const totalFvsPend = obraFiltro === 'all'
    ? Object.values(fvsPendPorObra).reduce((s, v) => s + v, 0)
    : (fvsPendPorObra[obraFiltro] || 0)
  const totalReqAberto = obraFiltro === 'all'
    ? reqEstoqueAbertas
    : (reqPorObra[obraFiltro] || 0)

  const obraNome = (obraId: string) => obras.find(o => o.id === obraId)?.nome || '—'
  const obraNames = Object.fromEntries(obras.map(o => [o.id, o.nome]))

  const handleObraClick = (obra: any) => { setObraAtiva(obra); navigate('/obra') }
  const handleNavigate = (path: string) => navigate(path)
  const handleOcClick = (obraId: string) => {
    const obra = obras.find(o => o.id === obraId)
    if (obra) { setObraAtiva(obra); navigate('/ocorrencias') }
  }
  const handlePendClick = (obraId: string) => {
    const obra = obras.find(o => o.id === obraId)
    if (obra) { setObraAtiva(obra); navigate('/pendencias') }
  }

  // Status bar chart (compact replacement for pie)
  const statusCounts: Record<string, number> = {}
  filteredObras.forEach(o => { statusCounts[o.status] = (statusCounts[o.status] || 0) + 1 })
  const statusLabels: Record<string, { lbl: string; cor: string }> = {
    planejamento: { lbl: 'Planejamento', cor: 'var(--sky)' },
    em_execucao: { lbl: 'Em Execução', cor: 'var(--amber)' },
    pausada: { lbl: 'Pausada', cor: 'var(--orange)' },
    concluida: { lbl: 'Concluída', cor: 'var(--emerald)' },
    cancelada: { lbl: 'Cancelada', cor: 'var(--rose)' },
  }

  const dateLabel = new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'hsl(var(--amber))', borderTopColor: 'transparent' }} />
      </div>
    )
  }

  return (
    <div className="space-y-5 animate-fade-up">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight text-foreground flex items-center gap-2">
            <Building2 size={20} className="text-primary" /> Visão Executiva
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">Consolidado de todas as obras</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <span className="text-[10px] text-muted-foreground">
              Atualizado em {lastUpdate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          <button onClick={load} className="p-1.5 rounded-lg hover:bg-muted transition-colors" title="Atualizar dados">
            <RefreshCw size={14} className="text-muted-foreground" />
          </button>
          <span className="pill pill-neutral text-[11px]">{dateLabel}</span>
        </div>
      </div>

      {/* Filtros */}
      <DashboardFilters
        periodo={periodo} setPeriodo={setPeriodo}
        obraFiltro={obraFiltro} setObraFiltro={setObraFiltro}
        obras={obras.map(o => ({ id: o.id, nome: o.nome }))}
      />

      {/* KPIs */}
      <DashboardKPIs
        data={{
          total_obras: rpcKpis?.total_obras ?? filteredObras.length,
          obras_ativas: rpcKpis?.obras_ativas ?? obrasAtivas.length,
          pendencias_abertas: rpcKpis?.pendencias_abertas ?? totalPendVenc,
          rdos_mes: rpcKpis?.rdos_mes ?? 0,
          fvs_aprovadas: rpcKpis?.fvs_aprovadas ?? 0,
          ocorrencias_abertas: rpcKpis?.ocorrencias_abertas ?? totalOcCrit,
        }}
        onNavigate={handleNavigate}
      />

      {/* Tabela de obras */}
      <DashboardObrasTable
        obraDetails={filteredObras}
        fvsPorObra={fvsPendPorObra}
        reqPorObra={reqPorObra}
        onObraClick={handleObraClick}
      />

      {/* Curva S + Status compacto */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <DashboardCurvaS
            obras={obras.map(o => ({ id: o.id, nome: o.nome }))}
            curvasPorObra={curvasPorObra}
            curvaConsolidada={curvaConsolidada}
          />
        </div>

        {/* Status compacto (substitui pizza) */}
        <div className="rounded-xl p-4 border" style={{ background: 'hsl(var(--bg2))', borderColor: 'hsl(var(--line))' }}>
          <div className="flex items-center gap-2 mb-4">
            <Activity size={14} className="text-primary" />
            <span className="text-sm font-bold text-foreground">Obras por Status</span>
          </div>
          <div className="space-y-2.5">
            {Object.entries(statusCounts).map(([status, count]) => {
              const s = statusLabels[status] || { lbl: status, cor: 'var(--bg5)' }
              const pct = filteredObras.length > 0 ? Math.round((count / filteredObras.length) * 100) : 0
              return (
                <div key={status}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ background: `hsl(${s.cor})` }} />
                      <span className="text-xs text-muted-foreground">{s.lbl}</span>
                    </div>
                    <span className="text-xs font-bold text-foreground">{count} ({pct}%)</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'hsl(var(--bg5))' }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: `hsl(${s.cor})` }} />
                  </div>
                </div>
              )
            })}
            {Object.keys(statusCounts).length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-4">Sem obras</p>
            )}
          </div>
        </div>
      </div>

      {/* Avanço por obra + FVS Conformidade */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Bar chart: avanço por obra */}
        <div className="rounded-xl p-4 border" style={{ background: 'hsl(var(--bg2))', borderColor: 'hsl(var(--line))' }}>
          <div className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
            <BarChart3 size={14} className="text-primary" /> Avanço Físico por Obra
          </div>
          <div className="space-y-2.5 max-h-[280px] overflow-y-auto">
            {filteredObras.filter(o => o.status !== 'cancelada').map(o => {
              const pct = o.avanco_fisico || 0
              const cor = pct >= 70 ? 'var(--emerald)' : pct >= 30 ? 'var(--amber)' : 'var(--rose)'
              return (
                <div key={o.id} className="flex items-center gap-3 cursor-pointer hover:opacity-80" onClick={() => handleObraClick(o)}>
                  <span className="text-xs text-foreground truncate w-[120px] flex-shrink-0 font-medium" title={o.nome}>
                    {o.nome.length > 18 ? o.nome.slice(0, 18) + '…' : o.nome}
                  </span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'hsl(var(--bg5))' }}>
                    <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: `hsl(${cor})` }} />
                  </div>
                  <span className="text-xs font-bold min-w-[36px] text-right" style={{ color: `hsl(${cor})` }}>{pct}%</span>
                </div>
              )
            })}
            {filteredObras.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">Sem obras</p>}
          </div>
        </div>

        {/* FVS Conformidade */}
        <DashboardFVSConformidade
          fvsAprovadas={fvsTotals.aprovadas}
          fvsReprovadas={fvsTotals.reprovadas}
          fvsPendentes={fvsTotals.pendentes}
          fvsPorObra={fvsPorObra}
          obraNames={obraNames}
        />
      </div>

      {/* Operacional + Financeiro */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <DashboardOperacional frentes={frentes} />
        <DashboardFinanceiro
          obras={filteredObras.map(o => ({
            id: o.id,
            nome: o.nome,
            valorContrato: o.valor_contrato || 0,
            avanco: o.avanco_fisico || 0,
          }))}
          valorComprasAberto={valorComprasAberto}
        />
      </div>

      {/* Alertas consolidados */}
      <DashboardAlerts
        ocCriticas={obraFiltro === 'all' ? ocCriticas : ocCriticas.filter(o => o.obra_id === obraFiltro)}
        pendVencidas={obraFiltro === 'all' ? pendVencidas : pendVencidas.filter(p => p.obra_id === obraFiltro)}
        semRdo={obraFiltro === 'all' ? semRdo : semRdo.filter(o => o.id === obraFiltro)}
        obraNome={obraNome}
        onOcorrenciaClick={handleOcClick}
        onPendenciaClick={handlePendClick}
        onObraClick={handleObraClick}
      />
    </div>
  )
}
